import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';

import { useGlobalStore } from '../state/globalStore';
import {
  ENTITY_TYPE,
  PRIVACY_POLICY_VERSION,
  VIMEO_PRIVACY_POLICY_LINK,
  VIMEO_TERMS_OF_SERVICE_LINK,
} from '../utils/constants';

export const usePrivacyPolicy = () => {
  const version = useGlobalStore((store) => store.leadCapture.privacyPolicyVersion || 1);
  const entityType = useGlobalStore((store) => store.entityType);
  const privacyPolicyUrl = useGlobalStore((store) => store.leadCapture.privacyPolicyUrl);
  const hasActiveCrmExportConnection = useGlobalStore(
    (store) => store.leadCapture.hasActiveCrmExportConnection,
  );

  const createPrivacyPolicyLink = useCallback(
    (text: string) => (
      <Anchor href={privacyPolicyUrl || VIMEO_PRIVACY_POLICY_LINK} target="_blank" rel="noreferrer">
        {text}
      </Anchor>
    ),
    [privacyPolicyUrl],
  );

  const createTAndCLink = useCallback(
    (text: string) => (
      <Anchor href={VIMEO_TERMS_OF_SERVICE_LINK} target="_blank" rel="noreferrer">
        {text}
      </Anchor>
    ),
    [],
  );

  const copyForVideo = useMemo(() => {
    /** For videos as of today, we don't have different copies or versions */
    if (Boolean(privacyPolicyUrl)) {
      return hasActiveCrmExportConnection
        ? translate({
            singular:
              "I understand that my viewing activity will be shared with the video owner and their connected service providers. I also acknowledge the video owner's {PP}Privacy Policy{/PP}.",
            replacements: {
              PP: createPrivacyPolicyLink,
            },
          })
        : translate({
            singular:
              "I understand that my viewing activity will be shared with the video owner and I acknowledge the video owner's {PP}Privacy Policy{/PP}.",
            replacements: {
              PP: createPrivacyPolicyLink,
            },
          });
    } else {
      return hasActiveCrmExportConnection
        ? translate(
            'I understand that my viewing activity will be shared with the video owner and their connected service providers.',
          )
        : translate('I understand that my viewing activity will be shared with the video owner.');
    }
  }, [createPrivacyPolicyLink, hasActiveCrmExportConnection, privacyPolicyUrl]);

  const copyForEvent = useMemo(() => {
    /**
     * For events as of today, we have 2 copies.
     * For events created before a certain date (PRIVACY_POLICY_V2_DATE_FOR_EVENT), we have a different copy.
     */
    switch (version) {
      case PRIVACY_POLICY_VERSION.V1:
        return translate({
          singular:
            'I agree that the host will see my viewing activities for this webinar, and I agree to {TS}Vimeo’s Terms of Service{/TS} and {PP}Privacy Policy{/PP}.',
          replacements: {
            TS: createTAndCLink,
            PP: createPrivacyPolicyLink,
          },
        });
      case PRIVACY_POLICY_VERSION.V2:
        if (Boolean(privacyPolicyUrl)) {
          return hasActiveCrmExportConnection
            ? translate({
                singular:
                  "I understand that my viewing activity will be shared with the event host and their connected service providers. I also acknowledge the host's {PP}Privacy Policy{/PP}.",
                replacements: {
                  PP: createPrivacyPolicyLink,
                },
              })
            : translate({
                singular:
                  "I understand that my viewing activity will be shared with the event host and I acknowledge the host's {PP}Privacy Policy{/PP}.",
                replacements: {
                  PP: createPrivacyPolicyLink,
                },
              });
        } else {
          return hasActiveCrmExportConnection
            ? translate(
                'I understand that my viewing activity will be shared with the event host and their connected service providers.',
              )
            : translate(
                'I understand that my viewing activity will be shared with the event host.',
              );
        }
    }
  }, [
    version,
    createTAndCLink,
    createPrivacyPolicyLink,
    privacyPolicyUrl,
    hasActiveCrmExportConnection,
  ]);

  const copy = useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPE.VIDEO:
        return copyForVideo;
      case ENTITY_TYPE.EVENT:
        return copyForEvent;
      default:
        return copyForEvent;
    }
  }, [entityType, copyForVideo, copyForEvent]);

  return useMemo(() => ({ version, copy }), [version, copy]);
};

const Anchor = styled.a`
  color: inherit;
`;
